<template>
  <div class="content">
    <PostBacklink />

    <section>
      <div class="row">
        <div class="column column-50">
          <h2 class="mt-0">
            How to update copy in a design file without feeling like you're
            defusing a bomb.
          </h2>
          <p>
            Often I see smart, competent people brought to their knees by a
            lowly text box. I can do nothing but watch helplessly, imagining a
            tiny drop of sweat falling away from their little avatar in the
            upper right corner. And so I put this together not for designers,
            but for everyone else who needs to jump in, make a change, and get
            out without detonating someone's layout.
          </p>
        </div>
      </div>
    </section>
    <section id="selecting-content">
      <div class="row">
        <div class="column">
          <h3 class="mt-0">Selecting Content</h3>
          <p>
            Honestly, this may be the only thing many people ever need to learn,
            and I'm often surprised by how many don't know about it.
          </p>
          <p>
            You can bypass Groups and Frames and click straight into their
            contents by holding
            <span class="shortcut-group"><ShortcutKey shortkey="Cmd" /></span>
            and clicking on a layer.
          </p>
          <p>That's it. You can probably skip the rest of this post.</p>
        </div>
        <div class="column">
          <div class="video-container">
            <video
              autoplay="true"
              loop="true"
              muted="true"
              playsinline="true"
              width="100%"
            >
              <source src="@/assets/posts/click-in.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </section>

    <section id="groups">
      <div class="row">
        <div class="column">
          <h3 class="mt-0">Groups</h3>
          <p>
            Groups and Frames are seemingly very similar. However, there are
            some subtle differences that can be confusing when used to working
            with groups in a traditional sense (Keynote, Canva, Miro, etc).
          </p>
          <p>
            A Group always matches the size of its contents, and resizing the
            Group will stretch everything inside proportionally. This is mostly
            helpful for moving, scaling, or rotating multiple layers at once.
          </p>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Create a Group"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'G' }])"
            />
            <ShortcutGridItem
              description="Ungroup"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'Shift' },
                  { type: 'key', value: 'G' },
                ])
              "
            />
          </div>
        </div>
        <div class="column">
          <div class="video-container">
            <video
              autoplay="true"
              loop="true"
              muted="true"
              playsinline="true"
              width="100%"
            >
              <source src="@/assets/posts/figma-groups.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </section>
    <section>
      <div class="row">
        <div class="column">
          <h3 class="mt-0">Frames</h3>
          <p class="pt-0 mt-0">
            Frames are more powerful containers that offer full layout control.
            To make matters easier, you can think of them as a window to the
            contents inside.
          </p>
          <p>
            Unlike Groups, Frames have defined boundaries. Typically a Frame
            clips the edges of its contents, hiding anything outside of the
            Frame's bounds.
          </p>
          <p>
            You can either draw a frame around existing content, or draw an
            empty Frame and drag stuff into it.
          </p>

          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Draw a Frame"
              :shortcut-items="convertShortcuts([{ type: 'key', value: 'G' }])"
            />
            <ShortcutGridItem
              description="Ungroup"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'Shift' },
                  { type: 'key', value: 'G' },
                ])
              "
            />
          </div>
        </div>
        <div class="column">
          <div class="video-container">
            <video
              autoplay="true"
              loop="true"
              muted="true"
              playsinline="true"
              width="100%"
            >
              <source src="@/assets/posts/frames.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </div>
    </section>
    <section>
      <div class="row">
        <div class="column">
          <h3 class="mt-0">Rearranging Content</h3>
          <p>
            Think of the Layers panel as one big lasagna. But unlike the real
            thing, you can arrange the ingredients after the fact.
          </p>
          <div class="shortcuts-grid">
            <ShortcutGridItem
              description="Move a Layer Up"
              :shortcut-items="convertShortcuts([{ type: 'key', value: ']' }])"
            />
            <ShortcutGridItem
              description="Move a Layer Down"
              :shortcut-items="convertShortcuts([{ type: 'key', value: '[' }])"
            />
            <ShortcutGridItem
              description="Move a Layer to the Top"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'Shift' },
                  { type: 'key', value: ']' },
                ])
              "
            />
            <ShortcutGridItem
              description="Move a Layer to the Bottom"
              :shortcut-items="
                convertShortcuts([
                  { type: 'key', value: 'Cmd' },
                  { type: 'key', value: 'Shift' },
                  { type: 'key', value: '[' },
                ])
              "
            />
          </div>
        </div>
        <div class="column">
          <div class="video-container">
            <video
              autoplay="true"
              loop="true"
              muted="true"
              playsinline="true"
              width="100%"
            >
              <source
                src="@/assets/posts/moving-up-and-down.mp4"
                type="video/mp4"
              />
            </video>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import PostBacklink from "@/components/posts/PostBacklink.vue";
import ShortcutKey from "@/components/shortcuts/ShortcutKey.vue";
import ShortcutGridItem from "@/components/shortcuts/ShortcutGridItem.vue";
import { ref } from "vue";

const platform = ref("mac");

const keyMappings = {
  mac: {
    Cmd: "Cmd",
    Option: "Option",
    Shift: "Shift",
    Ctrl: "Ctrl",
  },
  windows: {
    Cmd: "Ctrl",
    Option: "Alt",
    Shift: "Shift",
    Ctrl: "Ctrl",
  },
};

const convertShortcuts = (shortcuts) => {
  return shortcuts.map((shortcut) => {
    if (
      shortcut.type === "key" &&
      keyMappings[platform.value][shortcut.value]
    ) {
      return {
        ...shortcut,
        value: keyMappings[platform.value][shortcut.value],
      };
    }
    return shortcut;
  });
};
</script>

<script>
// Export metadata for automatic discovery
export const postMeta = {
  title: "Figma—not just for designers.",
  description:
    "A no-nonsense intro for marketers, writers, and anyone who needs to get in, get out, and not screw things up.",
  tags: ["Jun 24, 2025"],
  date: "2025-01-15",
  image: {
    type: "video",
    src: "@/assets/posts/click-in.mp4",
    mimeType: "video/mp4",
    alt: "Figma click-in demonstration",
  },
};
</script>

<style scoped lang="scss">
.shortcut-group {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.video-container {
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  background-color: #232323;
  position: relative;
  padding: 0;
  video {
    vertical-align: middle;
  }

  @media (max-width: 80rem) {
    padding: 0;
    margin-top: 1rem;
  }
}
.shortcuts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: center;
}
</style>
